import { useState, useEffect } from 'react';

import { SeasonSummaryCard } from '@/components/SeasonSummaryCard';

interface SeasonSummaryModalProps {
  open: boolean;
  onClose: () => void;
  seasonId?: number;
}

// Original card dimensions
const CARD_WIDTH = 728;
const CARD_HEIGHT = 895;

/**
 * Full-screen modal wrapper for the Season Summary flow
 *
 * Displays the season summary card in a modal overlay that appears
 * once for returning users who haven't seen the summary yet.
 *
 * The modal contains the full multi-card flow:
 * 1. SeasonSummaryCard (stats and share)
 * 2. SeasonNFTCard (NFT display and minting)
 * 3. SeasonCountdownCard (next season countdown)
 */
export function SeasonSummaryModal({
  open,
  onClose,
  seasonId = 1,
}: SeasonSummaryModalProps) {
  const [scale, setScale] = useState(1);

  // Calculate scale to fit card within viewport
  useEffect(() => {
    const calculateScale = () => {
      // Available space (minimal padding for maximum display)
      const availableWidth = window.innerWidth - 16; // 1rem padding
      const availableHeight = window.innerHeight - 16; // 1rem padding

      // Calculate scale factors for both dimensions
      const widthScale = availableWidth / CARD_WIDTH;
      const heightScale = availableHeight / CARD_HEIGHT;

      // Use the smaller scale to ensure card fits in both dimensions
      // Allow scaling up to 1.8x for larger displays, down as needed for small screens
      const calculatedScale = Math.min(widthScale, heightScale, 1.8);

      // Debug logging to verify scale calculation
      console.log('[SeasonSummaryModal] Scale calculation:', {
        availableWidth,
        availableHeight,
        widthScale: widthScale.toFixed(2),
        heightScale: heightScale.toFixed(2),
        finalScale: calculatedScale.toFixed(2),
        cardWidth: CARD_WIDTH,
        cardHeight: CARD_HEIGHT,
      });

      setScale(calculatedScale);
    };

    if (open) {
      calculateScale();
      window.addEventListener('resize', calculateScale);
      return () => window.removeEventListener('resize', calculateScale);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop with blur effect - clicking outside does NOT close */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

      {/* Content Container - Scales to fit viewport */}
      <div className="relative flex items-center justify-center">
        {/* Scaled Content Wrapper - Explicit dimensions for proper scaling */}
        <div
          style={{
            width: `${CARD_WIDTH}px`,
            height: `${CARD_HEIGHT}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            transition: 'transform 0.2s ease-out',
          }}
        >
          <SeasonSummaryCard seasonId={seasonId} onComplete={onClose} />
        </div>
      </div>
    </div>
  );
}
